import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, List, ListItem, ListItemText, ListItemIcon, Button, ListItemButton } from '@mui/material';
import { Box, Paper, Divider } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { usePrescriptions } from '../../../hooks/prescriptions/usePrescriptions';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import PrescriptionView from '../modals/PrescriptionView';
import { useDeleteAppointment } from '../../../hooks/appointments/useDeleteAppointment';
import { useDeletePrescription } from '../../../hooks/prescriptions/useDeletePrescription';
import { getPrescriptionById } from '../../../api/prescriptionService';
import CircularProgress from '@mui/material/CircularProgress';
import PrescriptionForm from '../forms/PrescriptionForm';
import { truncateString } from '../../../utils/truncateString';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function PrescriptionsListItems({ setListEmpty = null, setDoneLoading = null, history = null, historyEntry = null, pet = null, mode = 'history', upcoming = false, ref }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const  {prescriptions, loading, loadMore, searchFunction, resetParent } = usePrescriptions(history?.id, historyEntry?.id, pet?.id, mode, upcoming);

  const [selected, setSelected] = React.useState(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState('create');
  const [viewOpen, setViewOpen] = React.useState(false);

  const onSaveSuccess = async (updatedRecord) => {
    const updated = await getPrescriptionById(updatedRecord.id);
    setSelected(updated);
    resetParent();
  };

  const handleCreate = () => {
    setSelected(null);
    setFormMode('create');
    setFormOpen(true);
  };
  // Expose the function to parent
  useImperativeHandle(ref, () => ({
    handleCreate
  }));

  const handleEdit = (prescription) => {
    setSelected(prescription);
    setFormMode('edit');
    setFormOpen(true);
  };
  const handleDelete = async (prescription) => {
    try {
      await remove(prescription.id);
    } catch (err) {
      showSnackbar(t('prescriptions.messages.delete_fail'), "error");
    }
    
  };
  const onDeleteSuccess = () => {
    resetParent();
    showSnackbar(t('prescriptions.messages.delete_success'), "success");
  };
  const { remove } = useDeleteAppointment(onDeleteSuccess);

  const handleListItemClick = (prescription) => {
    setSelected(prescription);
    setViewOpen(true);
  };

  const handleListItemEdit = (prescription) => {
    handleEdit(prescription);
  };

  const handleListItemDelete = (prescription) => {
    handleDelete(prescription);
  };

  React.useEffect(() => {
    if (prescriptions.data.length !== 0)
    {
      if (setListEmpty) setListEmpty(false);
    }
  }, [prescriptions]);

  React.useEffect(() => {
    if (!loading)
    {
      if (setDoneLoading) setDoneLoading(true);
    }
  }, [loading]);

  return (
    <>
      {loading ? (
        <></>
      ) : (
      prescriptions?.data.map((prescription) => (
        <ListItem 
            key={"prescription" + prescription.id}
            sx={{ py: 0 }}
            alignItems="flex-start"
            secondaryAction={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <VerticalHamburgerMenu
                    options={[
                        { 
                            id: 1, 
                            name: 'View',
                            callback: () => handleListItemClick(prescription),
                            disabled: !userHasAccess(SUB_MODULES.PRESCRIPTION, 1)
                        },
                        { 
                            id: 2, 
                            name: 'Edit',
                            callback: () => handleListItemEdit(prescription),
                            disabled: !userHasAccess(SUB_MODULES.PRESCRIPTION, 2)
                        },
                        { 
                            id: 3, 
                            name: 'Delete',
                            callback: () => handleDelete(prescription),
                            confirmMessage: t('prescriptions.messages.delete_confirm'),
                            disabled: !userHasAccess(SUB_MODULES.PRESCRIPTION, 3)
                        }
                    ]}
                    />
                </Box>
            }
        >
            <ListItemButton 
              sx={{ py: 0 }} 
              onClick={() => {if (userHasAccess(SUB_MODULES.PRESCRIPTION, 1)) handleListItemClick(prescription)}}
            >
            <ListItemIcon>
              <MedicationLiquidIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ maxWidth: '90%' }}>
                  {`${truncateString(prescription.content, 50)}`}
                </Typography>
              }
              secondary={
                <Typography component="span" variant="body2" color="text.secondary">
                  {`${prescription.date}`}
                </Typography>
              }
            />
            </ListItemButton>
        </ListItem>
      )))}
      <PrescriptionForm
        mode={formMode}
        prescription={selected}
        successCallback={onSaveSuccess}
        history={history}
        historyEntry={historyEntry}
        pet={pet} 
        open={formOpen}
        onClose={() => setFormOpen(false)}
      />
      <PrescriptionView 
        handleEdit={handleEdit}
        prescription={selected}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
      />
    </>
  );
}